import { Button, FormHelperText, Grid, TextField } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import React, { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppSwitch from "components/Shared/AppSwitch";
import { useLazyGetDistrictsByRegionIdQuery } from "store/services/LocationsApi/districtApi";
import RichTextEditor from "components/Shared/RichTextEditor";
import { useGetDdlRegionListQuery } from "store/services/LocationsApi/regionApi";

const MunicipalityForm = ({
  municipalityDetail = null,
  isBusy = false,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();

  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      name: municipalityDetail?.name ?? "",
      code: municipalityDetail?.code ?? "",
      coordinates: municipalityDetail?.coordinates || { lat: "", long: "" },
      description: municipalityDetail?.description ?? "",
      status: municipalityDetail?.status === "Inactive" ? false : true,
      region: municipalityDetail?.region ?? "",
      district: municipalityDetail?.district ?? "",
    },
  });

  const _watchRegion = useWatch({ control, name: "region" });

  const { data: regionRes, isLoading: isLoadingRegions } =
    useGetDdlRegionListQuery();

  const [
    fetchDistricts,
    { data: districtRes = [], isLoading: loadingDistricts, fetchingDistrict },
  ] = useLazyGetDistrictsByRegionIdQuery();

  const handlePayload = (data) => {
    const _payload = {
      ...data,
      region: data.region?._id,
      district: data.district?._id,
      status: data.status ? "Active" : "Inactive",
    };

    onSubmit(_payload);
  };

  useEffect(() => {
    if (_watchRegion?._id) {
      fetchDistricts(_watchRegion._id);
      if (municipalityDetail?.region?._id !== _watchRegion._id) {
        setValue("district", "");
      }
    } else {
      setValue("district", "");
    }
  }, [_watchRegion, fetchDistricts, setValue, municipalityDetail]);

  React.useEffect(() => {
    if (municipalityDetail) {
      reset({
        name: municipalityDetail.name,
        code: municipalityDetail.code,
        coordinates: municipalityDetail.coordinates || { lat: "", long: "" },
        description: municipalityDetail.description,
        region: municipalityDetail.region ?? "",
        district: municipalityDetail.district ?? "",
        status: municipalityDetail.status === "Inactive" ? false : true,
      });
    }
  }, [municipalityDetail, reset]);

  const renderTextField = (name, label, type = "text", rules = {}) => (
    <Grid item md={6}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <TextField
            fullWidth
            label={label}
            type={type}
            size="small"
            {...field}
          />
        )}
      />
    </Grid>
  );

  return (
    <AppForm loading={loadingDistricts || fetchingDistrict}>
      <AppForm.Content>
        <Grid container spacing={3} paddingY={2}>
          <Grid item md={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Name"
                  type="text"
                  size="small"
                  error={!!errors?.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="code"
              control={control}
              rules={{
                required: "Code is required",
                maxLength: {
                  value: 8,
                  message: "Code must be at most 8 characters long",
                },
                validate: (value) => {
                  if (value !== value.toUpperCase()) {
                    return "Code must be in uppercase";
                  }
                  if (!/^[A-Z0-9]+$/.test(value)) {
                    return "Code can only contain uppercase letters and numbers";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  required
                  {...field}
                  fullWidth
                  label="Code"
                  type="text"
                  size="small"
                  error={!!errors?.code}
                  helperText={errors?.code?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="region"
              control={control}
              rules={{ required: "Region is required" }}
              render={({ field }) => (
                <>
                  {/* <pre>{JSON.stringify(field, null, 2)}</pre> */}
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Region"
                    loading={isLoadingRegions}
                    loadingText="Loading Regions..."
                    options={regionRes || []}
                    getOptionLabel={(opt) => opt?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    value={field.value || ""}
                    onChange={(_, data) => setValue("region", data)}
                    error={!!errors?.region}
                    helperText={errors?.region?.message}
                  />
                </>
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="district"
              control={control}
              rules={{
                required: _watchRegion?._id
                  ? "Please select a District"
                  : false,
              }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required={!!_watchRegion?._id}
                  disabled={!_watchRegion?._id}
                  label="District"
                  options={districtRes?.data ?? []}
                  loading={loadingDistricts}
                  loadingText="Loading Districts..."
                  getOptionLabel={(opt) => opt?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  value={field.value || null}
                  onChange={(_, data) => setValue("district", data)}
                  noOptionsText={
                    _watchRegion?._id
                      ? "No districts found"
                      : "Please select a region first"
                  }
                  error={!!errors?.district}
                  helperText={errors?.district?.message}
                />
              )}
            />
          </Grid>

          {renderTextField("coordinates.lat", "Latitude", "number", {})}
          {renderTextField("coordinates.long", "Longitude", "number", {})}

          <Grid item md={12}>
            <FormHelperText>Description</FormHelperText>

            <Controller
              name="description"
              control={control}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    municipalityDetail?.description
                      ? municipalityDetail?.description
                      : "<p>Enter Note Content.</p>"
                  }
                  onChange={onChange}
                  height={200}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <AppSwitch {...field} label="Is Active?" />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button disabled={isBusy} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit(handlePayload)}
        >
          {municipalityDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default MunicipalityForm;
